import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Divider, Modal, useTheme } from "@mui/material";
import ChatAgentMessageInput from "@components/ChatAgent/components/ChatAgentMessageInput/ChatAgentMessageInput";
import ChatAgentMessageBox from "@components/ChatAgent/components/ChatAgentMessageBox/ChatAgentMessageBox";
import { Close, SmartToy } from "@mui/icons-material";
import { useAuth } from "@context/auth";
import axios, { AxiosError } from "axios";
import ChatAgentErrorOverlay from "@components/ChatAgent/components/ChatAgentErrorOverlay/ChatAgentErrorOverlay";
import { getCookie } from "@utils/utilsCookie";
import { useTranslation } from "react-i18next";
import {
  CloseButton,
  DisclaimerText,
  HeaderAIIcon,
  HeaderLeft,
  HeaderSubtitle,
  HeaderTextContainer,
  HeaderTitle,
  InputFooterContainer,
  LoadingHistoryContainer,
  LoadingHistoryMessage,
  MessagesContainer,
  ModalContainer,
  ModalHeaderContainer,
  RootContainer,
  SessionTerminatedMessage,
  SessionTerminatedMessageContainer,
  WelcomeMessage,
  WelcomeMessageContainer,
} from "@components/ChatAgent/style";
import { useChatAgent } from "@hooks/useChatAgent";

export interface ChatAgentRequestPayload {
  session_id: string;
  session_attributes: {
    corporate_id: string;
  };
  payload: {
    end_session: boolean;
    user_message: string;
  };
}

export interface ChatAgentMessage {
  response: string;
  fist_message: boolean;
  end_session: boolean;
  sender: "user" | "assistant";
  from_history?: boolean;
  timestamp?: string;
}

export interface ChatAgentResponse {
  text: string;
}

const apiTimeout = 30000; //30 seconds

const ChatAgent = () => {
  const { user } = useAuth();
  const { t } = useTranslation("agent_chat");
  const theme = useTheme();
  const [messages, setMessages] = useState<ChatAgentMessage[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isRetrievingHistory, setIsRetrievingHistory] = useState(false);
  const [blockUserInput, setBlockUserInput] = useState(false);
  const [apiError, setApiError] = useState("");
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, closeChat: onClose, sessionId, corporateId } = useChatAgent();

  const apiInstance = axios.create({
    timeout: apiTimeout,
    headers: {
      Authorization: `Bearer ${user?.authToken}`,
      "x-ada-session-token": getCookie("sessionId"),
    },
  });

  useEffect(() => {
    if (
      messagesContainerRef.current &&
      messages &&
      messages.filter((message) => !message.from_history).length > 0
    ) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === "user") {
        setMessages([
          ...messages,
          {
            sender: "assistant",
            end_session: false,
            fist_message: false,
            response: "",
          },
        ]);
      } else {
        retrieveMessageFromAgent();
      }

      scrollToLastMessage();
    }
  }, [messages.length]);

  useEffect(() => {
    initChat();
    if (isOpen) getChatHistory();
  }, [isOpen]);

  useEffect(() => {
    if (!blockUserInput && messageInputRef.current)
      messageInputRef.current.focus();
  }, [blockUserInput]);

  const sendMessage = async (message: string) => {
    const endpoint = `${process.env.REACT_APP_AGENT_AI}learning_agent/admin/message`;

    const payload: ChatAgentRequestPayload = {
      session_id: sessionId,
      session_attributes: {
        corporate_id: corporateId,
      },
      payload: {
        end_session: false,
        user_message: message,
      },
    };

    setBlockUserInput(true);
    setIsSendingMessage(true);

    apiInstance
      .post(endpoint, payload)
      .then((res) => {
        setMessages([
          ...messages,
          {
            sender: "user",
            end_session: false,
            fist_message: true,
            response: message,
          },
        ]);
      })
      .catch((error) => {
        console.error(
          "[ChatAgent] An error occurred sending message to agent",
          error,
        );
        if (
          error.response &&
          error.response.data &&
          error.response.data.response
        ) {
          setApiError(error.response.data.response as string);
        } else if (error.message) {
          setApiError(`${error.message} [${error.code}]`);
        }
      })
      .finally(() => {
        setIsSendingMessage(false);
      });
  };

  const retrieveMessageFromAgent = async () => {
    const endpoint = `${process.env.REACT_APP_AGENT_AI}learning_agent/admin/response/stream?session_id=${sessionId}&corporate_id=${corporateId}`;

    try {
      //Axios doesn't support SSE streaming, using fetch
      const response = await fetch(endpoint, {
        method: "GET",
        cache: "no-cache",
        headers: {
          Authorization: `Bearer ${user?.authToken}`,
          "x-ada-session-token": getCookie("sessionId"),
          "Content-Type": "application/json",
        },
      });

      if (!response.body) {
        console.error("[ChatAgent] Stream not available");
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.includes("event: error")) {
            const updatedMessages = [...messages];
            updatedMessages[updatedMessages.length - 1].end_session = true;
            setMessages(updatedMessages);
          } else if (line.startsWith("data:")) {
            const jsonString = line.replace(/^data:\s*/, "");
            try {
              const data: ChatAgentResponse = JSON.parse(jsonString);
              const updatedMessages = [...messages];
              updatedMessages[updatedMessages.length - 1].response += data.text;

              setMessages(updatedMessages);

              scrollToLastMessage();
            } catch (err) {
              console.error(
                "[ChatAgent] An error occurred parsing JSON SSE:",
                err,
              );
            }
          }
        }
      }

      setBlockUserInput(false);
    } catch (error: any) {
      console.error(
        "[ChatAgent] An error has occurred while fetching agent's response:",
        error,
      );
      if (
        error.response &&
        error.response.data &&
        error.response.data.response
      ) {
        setApiError(error.response.data.response as string);
      } else if (error.message) {
        setApiError(`${error.message} [${error.code}]`);
      }
    }
  };

  const getChatHistory = () => {
    setIsRetrievingHistory(true);
    const endpoint = `${process.env.REACT_APP_AGENT_AI}learning_agent/admin/messages/${sessionId}?corporate_id=${corporateId}`;

    apiInstance
      .get(endpoint)
      .then((res) => {
        if (res.data && res.data.messages && res.data.messages.length > 0) {
          setMessages(
            res.data.messages.map((message: any) => {
              return {
                sender: message.role,
                response: message.text,
                fist_message: false,
                end_session: false,
                from_history: true,
              } as ChatAgentMessage;
            }),
          );

          scrollToLastMessage();
        }
      })
      .catch((error: AxiosError) => {
        if (error?.response?.status !== 404) {
          console.error(
            "[ChatAgent] An error occurred while retrieving agent chat history",
            error,
          );
        }
      })
      .finally(() => {
        setIsRetrievingHistory(false);
      });
  };

  const initChat = () => {
    setApiError("");
    setBlockUserInput(false);
    setMessages([]);
  };

  const scrollToLastMessage = () => {
    if (messagesContainerRef && messagesContainerRef.current) {
      setTimeout(() => {
        messagesContainerRef.current!.scrollTo({
          top: messagesContainerRef.current!.scrollHeight,
          behavior: "auto",
        });
      }, 300);
    }
  };

  const isSessionTerminated = messages.find((message) => message.end_session);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <RootContainer>
        <ModalContainer theme={theme}>
          <ModalHeaderContainer>
            <HeaderLeft>
              <HeaderAIIcon>AI</HeaderAIIcon>
              <HeaderTextContainer>
                <HeaderTitle>{t("title")}</HeaderTitle>
                <HeaderSubtitle>{t("subtitle")}</HeaderSubtitle>
              </HeaderTextContainer>
            </HeaderLeft>
            <CloseButton
              onClick={onClose}
              disabled={
                blockUserInput || isRetrievingHistory || isSendingMessage
              }
            >
              <Close fontSize={"medium"} />
            </CloseButton>
          </ModalHeaderContainer>

          <Divider />

          <MessagesContainer
            theme={theme}
            blockUserInput={blockUserInput}
            ref={messagesContainerRef}
            gap={"20px"}
          >
            {messages && messages.length > 0 ? (
              <>
                {messages.map((message, index) => (
                  <ChatAgentMessageBox
                    key={`chat_message_${index}`}
                    message={message}
                    user={user}
                    index={index}
                  />
                ))}
              </>
            ) : (
              <WelcomeMessageContainer gap={"20px"}>
                <HeaderAIIcon sx={{ width: "56px", height: "56px" }}>
                  <SmartToy sx={{ fontSize: "30px" }} />
                </HeaderAIIcon>
                <WelcomeMessage
                  dangerouslySetInnerHTML={{ __html: t("welcome") || "" }}
                />
                {isRetrievingHistory && (
                  <LoadingHistoryContainer>
                    <CircularProgress size={20} />
                    <LoadingHistoryMessage>
                      {t("retrieving_history")}
                    </LoadingHistoryMessage>
                  </LoadingHistoryContainer>
                )}
              </WelcomeMessageContainer>
            )}
          </MessagesContainer>

          {apiError && (
            <ChatAgentErrorOverlay
              error={apiError}
              onReset={initChat}
              onClose={onClose}
            />
          )}

          <Divider />

          {isSessionTerminated ? (
            <SessionTerminatedMessageContainer>
              <SessionTerminatedMessage>
                {t("session_terminated")}
              </SessionTerminatedMessage>
            </SessionTerminatedMessageContainer>
          ) : (
            <InputFooterContainer>
              <ChatAgentMessageInput
                isLoading={isSendingMessage}
                onSendMessage={(message) => sendMessage(message)}
                disabled={blockUserInput || !!apiError}
                inputRef={messageInputRef}
              />
              {t("disclaimer") && (
                <DisclaimerText>{t("disclaimer")}</DisclaimerText>
              )}
            </InputFooterContainer>
          )}
        </ModalContainer>
      </RootContainer>
    </Modal>
  );
};

export default ChatAgent;
